import React, { useState, useMemo, useEffect } from "react";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import { Button, Stack, Grid } from "@mui/material";
import URLMappingForm from "./URLMappingForm";
import "./URLMapping.css";
import { Typography } from "@mui/material";
import OZDataGrid from "../../../components/admin/common/OZDataGrid/OZDataGrid";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import DeleteDialog from '../../../components/admin/common/CustomDeleteDialog';
import OZGridToolbar from "../../../components/admin/common/OZDataGrid/OZGridToolbar";
import { showErrorNotification, showSuccessNotification, showWarningNotification, } from "../../../components/admin/common/NotiService";
import { writeAccess } from "../../../services/page-service";
import LoadingScreen from "../../../components/admin/common/OZLoadingScreen/OZLoadingScreen";
import { deleteURLMappingById, getURLMappingById, getURLMappings } from "../../../services/api-service";
import { checkRoles } from "../../../utils/util";

function URLMapping() {

  const CommentRenderer = (props) => {

    const [openDeleteConfirmationDialog, setDeleteConfirmationDialog] = useState(false);
    const [urlMappingAnchorEl, setURLMappingAnchorEl] = useState(null);
    const anchorRef = React.useRef();
    const [openEditDialog, setEditDialog] = useState(false);

    const onClickEdit = () => {
      setURLMappingAnchorEl(null);
      setEditDialog(true);
    };

    const onClickDelete = (e) => {
      setURLMappingAnchorEl(null);
      setDeleteConfirmationDialog(true);
    };

    const onMorevertIconClick = () => {
      setTimeout(() => setURLMappingAnchorEl(anchorRef?.current), 1);
    };

    const onMenuClose = () => {
      setURLMappingAnchorEl(null);
    };

    const onClickDeleteDialogClose = () => {
      setDeleteConfirmationDialog(false);
    };

    const onClickDeleteDialogDelete = () => {
      deleteURLMapping(props.data.id);
      setDeleteConfirmationDialog(false);
    };

    const styleListItemButton = useMemo(
      () => ({
        "&.MuiButtonBase-root": {
          color: '#212121',
          fontFamily: "Roboto",
          fontSize: "12px",
          fontWeight: 400,
          height: '16px',
          lineHeight: "14px",
        },
        '&:hover': {
          borderRadius: "4px",
        },
      }),
      []
    );

    return (
      <div className="oz-url-mapping-container">
        <Typography sx={{
          fontFamily: "Roboto",
          fontSize: "14px",
          fontWeight: 400,
          color: "#212121",
          lineHeight: "16.41px",
          maxWidth: "200px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>{props.value}</Typography>
        {writeAccess() && (checkRoles('ROLE_ADMIN')) &&
          <img className="urlMappingMorevertIcon"
            onClick={onMorevertIconClick}
            src={`${process.env.PUBLIC_URL}/icons/more_vert.svg`} alt="edit"
          >
          </img>
        }
        <div ref={anchorRef}>
          <Menu anchorEl={urlMappingAnchorEl} open={Boolean(urlMappingAnchorEl)} onClose={onMenuClose} sx={{
            "& .MuiPaper-root": {
              backgroundColor: "#FFFFFF",
              width: "133px",
              height: "62px",
            },
            "& .MuiMenu-paper": {
              backgroundColor: "#FFFFFF",
              boxShadow: "0rem 0.25rem 0.75rem rgba(0, 0, 0, 0.04)",
              border: "0.0625rem solid #E6E5E6",
              borderRadius: "12px",
              marginLeft: "-0.3625rem",
              marginTop: "0rem",
            },
          }}>
            <MenuItem onClick={onClickEdit} sx={{ ...styleListItemButton, marginTop: "1px" }}>Edit Mapping </MenuItem>
            <MenuItem onClick={onClickDelete} sx={{ ...styleListItemButton, marginTop: '9px' }} disabled={!writeAccess()}            >Delete Mapping</MenuItem>
          </Menu>
          {openDeleteConfirmationDialog &&
            <DeleteDialog title={"Delete URL Mapping"} content={" This will delete this " + props.data.sourceUrl + " URL mapping permanently. You cannot undo this action."} onClose={onClickDeleteDialogClose}
              open={true} deleteFunction={onClickDeleteDialogDelete} />
          }
          {openEditDialog &&
            <URLMappingForm updateMapping={getURLMappingList} onClose={() => { setEditDialog(false) }} data={props.data} editAccess={writeAccess()} />
          }
        </div>
      </div>
    );
  };

  const [urlMappingForm, setURLMappingForm] = useState({ isNew: false, show: false });
  const [urlMappingList, setURLMappingList] = useState([]);
  const [onRowClickURLMappingData, setOnRowClickURLMappingData] = useState(null);
  const [gridRef, setGridRef] = useState(null);
  const [loading, setLoading] = useState(false);

  const showURLMappingForm = () => {
    setURLMappingForm({ isNew: true, show: true });
  };

  const closeURLMappingForm = () => {
    setURLMappingForm({ isNew: false, show: false });
  };

  let buttonStyle = useMemo(
    () => ({
      borderRadius: "8px",
      height: "30px",
      fontSize: "14px",
      fontWeight: 400,
      width: "149px",
      backgroundcolor: "#3D8BF8",
      lineHeight: "16.41px",
    }),
    []
  );

  useEffect(() => {
    getURLMappingList();
  }, []);

  async function getURLMappingList() {
    setLoading(true);
    try {
      getURLMappings()
        .then((resp) => {
          setURLMappingList(resp.Data);
          setLoading(false);
        })
        .catch((e) => {
          setURLMappingList([]);
          setLoading(false);
        });
    } catch (e) {
      setLoading(false);
      showErrorNotification("Error in URL mapping list");
    }
  };

  async function deleteURLMapping(dataId) {
    try {
      deleteURLMappingById(dataId)
        .then((resp) => {
          if (resp.Status === "Success") {
            getURLMappingList();
            showSuccessNotification(resp.Message);
          }
          else {
            showWarningNotification(resp.Message);
          }
        })
        .catch((e) => {
          showErrorNotification("Error deleting URL mapping");
        });
    } catch (e) {
      showErrorNotification("Error in URL mapping delete");
    }
  };

  const columnDefs = [
    {
      field: "name",
      headerName: "Name",
    },
    {
      field: "domainName",
      headerName: "Domain Name",
    },
    {
      field: "localIp",
      headerName: "Local IP",
      cellRenderer: CommentRenderer,
    }
  ];

  function onClickRow(rowItem) {
    setOnRowClickURLMappingData({});
    setURLMappingForm({ isNew: false, show: true });
    try {
      getURLMappingById(rowItem.data.id)
        .then((resp) => {
          setOnRowClickURLMappingData(resp.Data);
        })
        .catch((e) => {
          setOnRowClickURLMappingData({});
        });
    } catch (e) {
      showErrorNotification("Error in URL mapping list ");
    }
  };

  return (
    <div className="ozAdminURLMappingsContainer">
      <Grid sx={{ marginBottom: "10px" }}>
        <Typography className="oz-page-header"> Configurations </Typography>
      </Grid>
      <Stack direction="row" sx={{ marginBottom: "16px" }} justifyContent="space-between">
        <Typography className="oz-page-heading"> URL Mappings </Typography>
        {writeAccess() && (checkRoles('ROLE_ADMIN')) &&
          <Button
            sx={buttonStyle}
            variant="contained"
            startIcon={<AddBoxOutlinedIcon />}
            onClick={showURLMappingForm}
          >
            New Mapping
          </Button>
        }
      </Stack>
      {urlMappingForm.show && urlMappingForm.isNew && (<URLMappingForm urlMapping={getURLMappingList} onClose={closeURLMappingForm} />)}
      {urlMappingForm.show && !urlMappingForm.isNew && (
        <URLMappingForm openDialog={true} updateMapping={getURLMappingList} onClose={closeURLMappingForm} data={onRowClickURLMappingData}
          editAccess={writeAccess() && (checkRoles('ROLE_ADMIN'))} />
      )}
      <OZGridToolbar title='List of URL Mappings' searchPlaceholder='Search mappings' rowCount={urlMappingList.length} dataGridRef={gridRef} fileName='URLMappings' refreshGrid={getURLMappingList} />
      {loading ?
        (
          <div style={{ position: 'fixed', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: '0,0,0,0.5', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} >
            <LoadingScreen />
          </div>
        ) :
        (<>
          <OZDataGrid rowClickCallback={onClickRow} height="26.3rem" data={urlMappingList} columns={columnDefs} setGridRef={setGridRef} />
        </>
        )
      }
    </div>
  );
};

export default URLMapping;